import { useQuery } from '@tanstack/react-query';

import type { Member, Project, Workflow } from '@tasks/contracts';

import { getProject, listMembers } from '../api/projects';
import { queryKeys } from '../api/queryKeys';
import { getWorkflow } from '../api/workflow';

/** Проект текущего маршрута. `key` приходит из `useParams`. */
export function useProject(key: string | undefined): {
  readonly project: Project | null;
  readonly isLoading: boolean;
  readonly error: unknown;
} {
  const query = useQuery({
    queryKey: queryKeys.projects.detail(key ?? ''),
    queryFn: ({ signal }) => getProject(key as string, signal),
    enabled: Boolean(key),
    staleTime: 60_000,
  });
  return { project: query.data ?? null, isLoading: query.isPending, error: query.error };
}

export function useProjectMembers(key: string | undefined): readonly Member[] {
  const query = useQuery({
    queryKey: queryKeys.projects.members(key ?? ''),
    queryFn: ({ signal }) => listMembers(key as string, signal),
    enabled: Boolean(key),
    staleTime: 60_000,
  });
  return query.data ?? [];
}

export function useProjectWorkflow(key: string | undefined): Workflow | null {
  const query = useQuery({
    queryKey: queryKeys.projects.workflow(key ?? ''),
    queryFn: ({ signal }) => getWorkflow(key as string, signal),
    enabled: Boolean(key),
    staleTime: 60_000,
  });
  return query.data ?? null;
}

/** Права: Project Admin управляет workflow, участниками и настройками. */
export function useProjectPermissions(project: Project | null): {
  readonly canManageProject: boolean;
  readonly canEditIssues: boolean;
} {
  const role = project?.myRole ?? null;
  const isArchived = project?.isArchived ?? false;
  return {
    canManageProject: role === 'project_admin' && !isArchived,
    canEditIssues: role !== null && !isArchived,
  };
}
